import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { Account } from '@/modules/accounts/schemas'
import { useToast } from '@/infra/composables'
import type { OAuthProvider, SignupRequest } from './schemas'
import { authService } from './service'
import { tokenStorage } from './storage'
import { decodeJwt } from './jwt'

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(tokenStorage.get())
  const account = ref<Account | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const toast = useToast()

  const isAuthenticated = computed(() => !!token.value)
  const isAdmin = computed(() => {
    if (!token.value) {
      return false
    }
    const payload = decodeJwt(token.value)
    return payload?.is_admin ?? false
  })

  async function login(credentials: SignupRequest): Promise<void> {
    loading.value = true
    error.value = null
    try {
      const response = await authService.login(credentials)
      setToken(response.access_token)
      await fetchAccount()
      toast.success('Welcome back!')
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Login failed'
      toast.error(error.value)
      throw e
    } finally {
      loading.value = false
    }
  }

  async function loginWithOAuth(provider: OAuthProvider, redirectTo?: string): Promise<void> {
    loading.value = true
    error.value = null
    try {
      const response = await authService.getOAuthRedirect(provider)
      sessionStorage.setItem('oauth_state', response.state)
      sessionStorage.setItem('oauth_nonce', response.nonce)
      sessionStorage.setItem('oauth_provider', provider)
      if (redirectTo) {
        sessionStorage.setItem('oauth_redirect', redirectTo)
      }
      window.location.href = response.url
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'OAuth redirect failed'
      toast.error(error.value)
      loading.value = false
      throw e
    }
  }

  function clearOAuthSession(): void {
    sessionStorage.removeItem('oauth_state')
    sessionStorage.removeItem('oauth_nonce')
    sessionStorage.removeItem('oauth_provider')
  }

  async function handleOAuthCallback(code: string, state: string): Promise<void> {
    loading.value = true
    error.value = null

    const savedState = sessionStorage.getItem('oauth_state')
    const nonce = sessionStorage.getItem('oauth_nonce')
    const provider = sessionStorage.getItem('oauth_provider') as OAuthProvider

    if (state !== savedState) {
      error.value = 'Invalid OAuth state'
      toast.error(error.value)
      loading.value = false
      throw new Error(error.value)
    }

    try {
      const response = await authService.handleOAuthCallback(
        provider,
        code,
        state,
        nonce ?? undefined
      )
      setToken(response.access_token)
      await fetchAccount()

      clearOAuthSession()
      toast.success('Welcome back!')
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'OAuth callback failed'
      toast.error(error.value)
      throw e
    } finally {
      loading.value = false
    }
  }

  async function fetchAccount(): Promise<void> {
    loading.value = true
    error.value = null
    try {
      account.value = await authService.fetchAccount()
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to fetch account'
      toast.error(error.value)
      throw e
    } finally {
      loading.value = false
    }
  }

  async function deleteAccount(): Promise<void> {
    loading.value = true
    error.value = null
    try {
      await authService.deleteAccount()
      toast.success('Account deleted successfully')
      logout()
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to delete account'
      toast.error(error.value)
      throw e
    } finally {
      loading.value = false
    }
  }

  function setToken(newToken: string): void {
    token.value = newToken
    tokenStorage.set(newToken)
  }

  function logout(): void {
    token.value = null
    account.value = null
    tokenStorage.remove()
  }

  return {
    token: computed(() => token.value),
    account: computed(() => account.value),
    loading: computed(() => loading.value),
    error: computed(() => error.value),
    isAuthenticated,
    isAdmin,
    login,
    loginWithOAuth,
    handleOAuthCallback,
    fetchAccount,
    deleteAccount,
    logout,
  }
})
